import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './IntegrationApplicationMasterData.css';

interface ResourceStats {
  resourcePools: number;
  totalCapacity: number;
  allocations: number;
  totalAllocatedFte: number;
  skillsets: number;
}

const ResourceManagement: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ResourceStats>({
    resourcePools: 0,
    totalCapacity: 0,
    allocations: 0,
    totalAllocatedFte: 0,
    skillsets: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [capacityRes, allocationRes, skillsetRes] = await Promise.all([
        fetch('/api/resource-capacity'),
        fetch('/api/resource-allocation'),
        fetch('/api/skillset-master')
      ]);

      const capacityData = await capacityRes.json();
      const allocationData = await allocationRes.json();
      const skillsetData = await skillsetRes.json();

      // Calculate stats
      const resourcePools = Array.isArray(capacityData) ? capacityData.length : 0;
      const totalCapacity = Array.isArray(capacityData)
        ? capacityData.reduce((sum: number, r: any) => sum + (parseFloat(r.available_capacity) || 0), 0)
        : 0;

      const allocations = Array.isArray(allocationData) ? allocationData.length : 0;
      const totalAllocatedFte = Array.isArray(allocationData)
        ? allocationData.reduce((sum: number, r: any) => sum + (parseFloat(r.allocated_fte) || 0), 0)
        : 0;

      const skillsets = skillsetData?.data ? skillsetData.data.length : (Array.isArray(skillsetData) ? skillsetData.length : 0);

      setStats({
        resourcePools,
        totalCapacity,
        allocations,
        totalAllocatedFte,
        skillsets
      });
    } catch (error) {
      console.error('Error fetching resource stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Resource Management</h1>
            <p className="page-header__subtitle">
              Manage resource capacity, allocation, and skillsets for migration projects
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

        {/* Navigation Cards */}
        <div className="md-card">
          <div className="md-card-content">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '24px'
            }}>

              {/* Resource Capacity Card */}
              <div
                className="md-card md-card--elevated"
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '1px solid #e0e0e0'
                }}
                onClick={() => navigate('/integration-framework/resource-capacity')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div className="md-card-content" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: '#e8f5e9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      ⚡
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a' }}>
                        Resource Capacity
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                        Define resource pools and capacity
                      </div>
                    </div>
                  </div>
                  <div style={{
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                        {loading ? '...' : stats.resourcePools}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888' }}>Resource Pools</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resource Allocation Card */}
              <div
                className="md-card md-card--elevated"
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '1px solid #e0e0e0'
                }}
                onClick={() => navigate('/integration-framework/resource-allocation')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div className="md-card-content" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: '#ede7f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      📊
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a' }}>
                        Resource Allocation
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                        Allocate resources to applications
                      </div>
                    </div>
                  </div>
                  <div style={{
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>
                        {loading ? '...' : stats.allocations}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888' }}>Allocations</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resource Skillsets Card */}
              <div
                className="md-card md-card--elevated"
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: '1px solid #e0e0e0'
                }}
                onClick={() => navigate('/integration-framework/resource-skillsets')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div className="md-card-content" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      🎯
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a' }}>
                        Skillset Master
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                        Define and manage skillsets
                      </div>
                    </div>
                  </div>
                  <div style={{
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#2196f3' }}>
                        {loading ? '...' : stats.skillsets}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888' }}>Skillsets Defined</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '13px',
                        color: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        View All →
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ResourceManagement;
